import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BadgeIndianRupee, Clock, Edit3, Package, RefreshCw, Store, Utensils } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { restaurantAdminService, rowsOf, unwrap } from "../../services/restaurantAdmin.service";
import { isBase64Image, resolveRestaurantFoodImageUrl } from "../../utils/restaurantFoodImageUrl";
import { Button, Card, EmptyState, ErrorBox, LoadingBox, PageHeader, StatusPill, TableShell, formatCurrency, restaurantIdOf } from "./components/FoodAdminUi";

function valueOf(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "-";
}

function numberOf(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function foodId(row) {
  return row?.foodItemId || row?.food_item_id || row?.id || row?._id || "";
}

function foodName(row) {
  return row?.name || row?.foodName || row?.food_name || "Food item";
}

function foodImage(row) {
  const raw = row?.imageUrl || row?.image_url || row?.primaryImageUrl || row?.primary_image_url || row?.variants?.[0]?.imageUrl || row?.variants?.[0]?.image_url || "";
  return raw && !isBase64Image(raw) ? resolveRestaurantFoodImageUrl(raw) : "";
}

function restaurantName(row) {
  return row?.restaurantName || row?.restaurant_name || row?.name || "Restaurant";
}

function variantRows(row) {
  const direct = row?.variants || row?.variantRows || row?.variant_rows || row?.variantMappings || row?.variant_mappings;
  if (Array.isArray(direct)) return direct;
  const meta = row?.meta || {};
  if (Array.isArray(meta.variants)) return meta.variants;
  return [];
}

function tagsOf(row) {
  const raw = row?.tags || row?.tagNames || row?.tag_names || [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
    return raw.split(",").map((x) => x.trim()).filter(Boolean);
  }
  return [];
}

function defaultPrice(row) {
  const v = variantRows(row).find((x) => x.isDefault || x.is_default) || variantRows(row)[0] || {};
  return numberOf(v.sellingPrice ?? v.selling_price ?? v.price ?? row?.basePrice ?? row?.base_price ?? row?.sellingPrice ?? row?.selling_price, 0);
}

function defaultMrp(row) {
  const v = variantRows(row).find((x) => x.isDefault || x.is_default) || variantRows(row)[0] || {};
  return numberOf(v.mrp ?? row?.mrp, 0);
}

function defaultTax(row) {
  const v = variantRows(row).find((x) => x.isDefault || x.is_default) || variantRows(row)[0] || {};
  return numberOf(v.taxPercent ?? v.tax_percent ?? row?.taxPercent ?? row?.tax_percent, 0);
}

function variantName(v) {
  return v?.name || v?.variantName || v?.variant_name || v?.label || v?.size || v?.unit || "Variant";
}

function variantSku(v) {
  return v?.sku || v?.variantSku || v?.variant_sku || "-";
}

function variantImage(v) {
  const raw = v?.imageUrl || v?.image_url || v?.images?.[0]?.url || v?.image || "";
  return raw && !isBase64Image(raw) ? resolveRestaurantFoodImageUrl(raw) : "";
}

function DetailLine({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[#f2c6cc] bg-[#fffafb] p-3">
      <div className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 break-words text-[13px] font-bold text-slate-800">{value || "-"}</div>
    </div>
  );
}

async function runInChunks(items, chunkSize, worker) {
  const output = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    const results = await Promise.allSettled(chunk.map(worker));
    output.push(...results);
  }
  return output;
}

export default function FoodItemDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [food, setFood] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sellerLoading, setSellerLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadFood() {
    if (!id) return null;
    try {
      const res = await restaurantAdminService.getFoodItem(id);
      const row = unwrap(res);
      if (row?.id || row?.foodItemId || row?.food_item_id || row?.name) return row;
    } catch {}

    const listRes = await restaurantAdminService.listFoodItems({ limit: 1000 });
    const rows = rowsOf(listRes);
    return rows.find((item) => String(foodId(item)) === String(id)) || null;
  }

  async function load() {
    if (!id) return;
    setLoading(true);
    setError("");
    setSellerLoading(true);
    try {
      const [foodRow, restaurantRes] = await Promise.all([
        loadFood(),
        restaurantAdminService.listRestaurants({ limit: 1000 }),
      ]);
      if (!foodRow) throw new Error("Food item not found");
      const restaurantRows = rowsOf(restaurantRes);
      setFood(foodRow);
      setRestaurants(restaurantRows);

      const settled = await runInChunks(restaurantRows, 8, async (restaurant) => {
        const rid = restaurantIdOf(restaurant);
        if (!rid) return [];
        const res = await restaurantAdminService.listRestaurantFoodItems(rid, { limit: 1000 });
        return rowsOf(res)
          .filter((item) => String(foodId(item)) === String(id))
          .map((item) => ({ ...item, restaurant }));
      });

      const found = settled
        .filter((item) => item.status === "fulfilled")
        .flatMap((item) => Array.isArray(item.value) ? item.value : []);
      setSellers(found);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setSellerLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const variants = useMemo(() => variantRows(food), [food]);
  const image = foodImage(food);
  const tags = tagsOf(food);

  if (loading) return <LoadingBox label="Loading food details..." />;

  return (
    <div>
      <PageHeader
        eyebrow="Catalog Detail"
        icon={<Utensils size={14} />}
        title={foodName(food)}
        subtitle="Full catalog information, variants and every restaurant currently selling this item."
        actions={(
          <>
            <Button variant="ghost" onClick={() => navigate("/admin/food-dashboard/items")}><ArrowLeft size={16} /> Back</Button>
            <Button variant="ghost" onClick={load}><RefreshCw size={16} /> Refresh</Button>
            <Button onClick={() => navigate(`/admin/food-dashboard/items/${id}/edit`)}><Edit3 size={16} /> Edit Item</Button>
          </>
        )}
      />
      <ErrorBox error={error} onRetry={load} />

      <div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-[360px_1fr]">
        <Card>
          <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-[18px] border border-[#f2c6cc] bg-[#fffafb]">
            {image ? <img src={image} alt={foodName(food)} className="h-full w-full object-contain p-3" /> : <Package size={54} className="text-[#d86a78]" />}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <StatusPill value={Boolean(food?.isActive ?? food?.is_active ?? true) ? "Active" : "Inactive"} />
            <StatusPill value={food?.foodType || food?.food_type || "Food"} />
            {variants.length ? <StatusPill value={`${variants.length} Variants`} /> : null}
          </div>
        </Card>

        <Card>
          <h3 className="mb-4 text-[20px] font-extrabold text-slate-900">Food information</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <DetailLine label="SKU" value={valueOf(food?.sku)} />
            <DetailLine label="Category" value={valueOf(food?.categoryName, food?.category_name, food?.category)} />
            <DetailLine label="Subcategory" value={valueOf(food?.subcategoryName, food?.subcategory_name, food?.subCategoryName, food?.sub_category_name)} />
            <DetailLine label="Base / Selling Price" value={formatCurrency(defaultPrice(food))} />
            <DetailLine label="MRP" value={formatCurrency(defaultMrp(food))} />
            <DetailLine label="Tax" value={`${defaultTax(food)}%`} />
            <DetailLine label="Mapped Restaurants" value={food?.mappedCount ?? food?.mapped_count ?? sellers.length ?? 0} />
            <DetailLine label="Available Restaurants" value={food?.availableCount ?? food?.available_count ?? sellers.filter((s) => Boolean(s.isAvailable ?? s.is_available ?? true)).length} />
            <DetailLine label="Created At" value={food?.createdAt || food?.created_at ? new Date(food?.createdAt || food?.created_at).toLocaleString() : "-"} />
          </div>
          {food?.description ? <p className="mt-4 rounded-[14px] bg-[#fffafb] p-4 text-[13px] leading-relaxed text-slate-600">{food.description}</p> : null}
          {tags.length ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {tags.map((tag) => <span key={tag} className="rounded-full bg-[#fff0f2] px-3 py-1 text-[11px] font-bold text-[#d86a78]">{tag}</span>)}
            </div>
          ) : null}
        </Card>
      </div>

      <Card className="mb-5">
        <h3 className="mb-4 flex items-center gap-2 text-[18px] font-extrabold text-slate-900"><Package size={18} className="text-[#d86a78]" /> Variants</h3>
        {variants.length ? (
          <TableShell columns={["Image", "Variant", "SKU", "MRP", "Selling Price", "Tax", "Default", "Status"]} minWidth={920}>
            {variants.map((variant, index) => {
              const img = variantImage(variant);
              return (
                <tr key={variant.id || variant.variantId || variant.variant_id || index} className="border-b last:border-0">
                  <td className="px-4 py-4">
                    <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-[12px] border border-[#f2c6cc] bg-white">
                      {img ? <img src={img} alt="" className="h-full w-full object-contain p-1" /> : <Package size={16} className="text-[#d86a78]" />}
                    </div>
                  </td>
                  <td className="px-4 py-4 font-extrabold text-slate-900">{variantName(variant)}</td>
                  <td className="px-4 py-4">{variantSku(variant)}</td>
                  <td className="px-4 py-4">{formatCurrency(variant.mrp ?? 0)}</td>
                  <td className="px-4 py-4">{formatCurrency(variant.sellingPrice ?? variant.selling_price ?? variant.price ?? 0)}</td>
                  <td className="px-4 py-4">{numberOf(variant.taxPercent ?? variant.tax_percent ?? food?.taxPercent ?? food?.tax_percent, 0)}%</td>
                  <td className="px-4 py-4"><StatusPill value={Boolean(variant.isDefault ?? variant.is_default) ? "Yes" : "No"} /></td>
                  <td className="px-4 py-4"><StatusPill value={Boolean(variant.isActive ?? variant.is_active ?? true) ? "Active" : "Inactive"} /></td>
                </tr>
              );
            })}
          </TableShell>
        ) : <EmptyState title="No variant detail found" subtitle="Current food API did not return variant rows for this item." icon={<Package size={22} />} />}
      </Card>

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-[18px] font-extrabold text-slate-900"><Store size={18} className="text-[#d86a78]" /> Restaurants selling this food</h3>
            <p className="mt-1 text-[12px] text-slate-500">Calculated by checking restaurant-food mappings .</p>
          </div>
          <StatusPill value={sellerLoading ? "Loading" : `${sellers.length} Sellers`} />
        </div>
        {sellers.length ? (
          <TableShell columns={["Restaurant", "Restaurant Price", "Availability", "Mapping Status", "Prep Time", "Actions"]} minWidth={980}>
            {sellers.map((seller, index) => {
              const restaurant = seller.restaurant || {};
              const rid = restaurantIdOf(restaurant) || seller.restaurantId || seller.restaurant_id;
              return (
                <tr key={`${rid}-${seller.mappingId || seller.mapping_id || index}`} className="border-b last:border-0">
                  <td className="px-4 py-4">
                    <button type="button" onClick={() => rid && navigate(`/admin/food-dashboard/restaurants/${rid}`)} className="text-left font-extrabold text-slate-900 hover:text-[#d86a78] hover:underline">{restaurantName(restaurant)}</button>
                    <div className="mt-1 text-[12px] text-slate-500">{valueOf(restaurant.city, restaurant.address?.city)} · {valueOf(restaurant.phone)}</div>
                  </td>
                  <td className="px-4 py-4">{formatCurrency(seller.restaurantPrice ?? seller.restaurant_price ?? seller.price ?? seller.basePrice ?? seller.base_price)}</td>
                  <td className="px-4 py-4"><StatusPill value={Boolean(seller.isAvailable ?? seller.is_available ?? true) ? "Available" : "Unavailable"} /></td>
                  <td className="px-4 py-4"><StatusPill value={Boolean(seller.mappingActive ?? seller.mapping_active ?? true) ? "Active" : "Inactive"} /></td>
                  <td className="px-4 py-4"><Clock size={14} className="mr-1 inline text-[#d86a78]" />{valueOf(seller.preparationTimeMins, seller.preparation_time_mins, restaurant.preparationTimeMins, restaurant.preparation_time_mins)} mins</td>
                  <td className="px-4 py-4 text-right"><Button variant="ghost" size="sm" onClick={() => rid && navigate(`/admin/food-dashboard/restaurants/${rid}`)}>View Restaurant</Button></td>
                </tr>
              );
            })}
          </TableShell>
        ) : <EmptyState title="No restaurant seller found" subtitle={restaurants.length ? "This food is not mapped to any restaurant yet." : "No restaurants were loaded to check sellers."} icon={<Store size={22} />} />}
      </Card>
    </div>
  );
}
